import React, { useState, useEffect, useCallback } from "react";
import { supabase } from "../supabase";
import { Input, Textarea, Select, Btn, PageHeader, Card, Badge, FilterPill, TabBar, Switch, EmptyState, TextLink} from "../ui";
import { normalizeEmail, escapeFilterValue } from "../utils/helpers";
import { pmError } from "../utils/errors";
import { Spinner } from "./shared";

// Push subscription control. Replaces the old DevicePushPanel which
// surfaced raw permission state, the VAPID endpoint URL, and a 4-button
// debug bar — accurate but read like a DevTools console. The actual
// auto-subscribe still runs silently from App.js:registerPushNotifications
// on login; this panel is for the manual re-enable / test / disable
// flow when something went wrong silently.
function DevicePushPanel({ companyId, userProfile, showToast }) {
  const [state, setState] = useState({ permission: "unknown", subscribed: false, endpoint: null, log: [], health: null });
  const [busy, setBusy] = useState(false);

  const refresh = useCallback(async () => {
    if (typeof window === "undefined") return;
    const permission = (typeof Notification !== "undefined") ? Notification.permission : "unsupported";
    let subscribed = false, endpoint = null;
    try {
      const reg = await navigator.serviceWorker.getRegistration?.();
      if (reg?.pushManager) {
        const sub = await reg.pushManager.getSubscription();
        if (sub) { subscribed = true; endpoint = sub.endpoint; }
      }
    } catch (_e) { /* not supported */ }
    // Pull health from server: when did the SW last beacon back?
    // null/old means Apple silently revoked; user should re-enable.
    let health = null;
    try {
      if (companyId && userProfile?.email) {
        const { data } = await supabase.from("push_subscriptions")
          .select("last_sw_received_at, last_dispatch_at, dead_marked_at, created_at")
          .eq("company_id", companyId).eq("user_email", userProfile.email)
          .maybeSingle();
        health = data || null;
      }
    } catch (_e) { /* best effort */ }
    setState(s => ({ ...s, permission, subscribed, endpoint, health }));
  }, [companyId, userProfile?.email]);

  useEffect(() => { refresh(); }, [refresh]);

  async function handleEnable() {
    if (busy) return;
    setBusy(true);
    const log = [];
    const add = (line) => log.push(new Date().toLocaleTimeString() + " " + line);
    try {
      add("Checking browser support…");
      if (!("serviceWorker" in navigator)) { add("❌ service worker not supported"); throw new Error("service worker unsupported"); }
      if (!("PushManager" in window)) { add("❌ push manager not supported"); throw new Error("push unsupported"); }
      add("✓ browser supports push");

      add("Registering /sw.js…");
      const registration = await navigator.serviceWorker.register("/sw.js");
      add("✓ service worker registered");

      add("Requesting Notification permission…");
      const permission = await Notification.requestPermission();
      add(permission === "granted" ? "✓ permission granted" : "❌ permission " + permission);
      if (permission !== "granted") throw new Error("permission " + permission);

      const key = process.env.REACT_APP_VAPID_PUBLIC_KEY || "";
      if (!key) { add("❌ VAPID key missing from build"); throw new Error("VAPID key missing"); }
      add("✓ VAPID public key present");

      const padding = "=".repeat((4 - key.length % 4) % 4);
      const base64 = (key + padding).replace(/-/g, "+").replace(/_/g, "/");
      const rawData = window.atob(base64);
      const applicationServerKey = Uint8Array.from([...rawData].map(c => c.charCodeAt(0)));

      const existing = await registration.pushManager.getSubscription();
      if (existing) {
        add("Existing subscription — unsubscribing first (VAPID rotation safe)");
        try { await existing.unsubscribe(); } catch (_e) { add("⚠︎ unsubscribe refused — continuing"); }
      }

      add("Subscribing…");
      const subscription = await registration.pushManager.subscribe({ userVisibleOnly: true, applicationServerKey });
      add("✓ subscribed");

      add("Saving subscription…");
      const { error } = await supabase.from("push_subscriptions").upsert([{
        company_id: companyId,
        user_email: userProfile?.email,
        subscription: JSON.parse(JSON.stringify(subscription)),
      }], { onConflict: "company_id,user_email" });
      if (error) { add("❌ save failed: " + error.message); throw error; }
      add("✓ saved — push is live on this device");
      if (showToast) showToast("Push notifications turned on for this device.", "success");
      await refresh();
    } catch (e) {
      add("ERROR: " + (e?.message || "unknown"));
      if (showToast) showToast("Couldn't turn on notifications: " + (e?.message || "unknown"), "error");
    } finally {
      setState(s => ({ ...s, log }));
      setBusy(false);
    }
  }

  async function handleTest() {
    if (!userProfile?.email || !companyId) return;
    try {
      const { data: session } = await supabase.auth.getSession();
      const jwt = session?.session?.access_token;
      if (!jwt) { showToast("Not signed in — can't test", "error"); return; }
      const res = await fetch("/api/notifications?action=push", {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: "Bearer " + jwt },
        body: JSON.stringify({ company_id: companyId, user_email: userProfile.email, title: "Housify test", body: "Push is working on this device.", url: "/notifications" }),
      });
      const j = await res.json().catch(() => ({}));
      if (!res.ok) { showToast("Test push failed: " + (j.error || res.status), "error"); return; }
      showToast(j.delivered > 0 ? "Test push delivered." : "Test push sent but not delivered yet.", j.delivered > 0 ? "success" : "warning");
    } catch (e) {
      showToast("Test push failed: " + e.message, "error");
    }
  }

  async function handleDisable() {
    if (busy) return;
    setBusy(true);
    try {
      const reg = await navigator.serviceWorker.getRegistration?.();
      const sub = reg?.pushManager ? await reg.pushManager.getSubscription() : null;
      if (sub) {
        try { await sub.unsubscribe(); } catch (_e) { /* harmless if already gone */ }
      }
      await supabase.from("push_subscriptions").delete()
        .eq("company_id", companyId)
        .ilike("user_email", escapeFilterValue(userProfile?.email || ""));
      showToast?.("Push notifications turned off for this device.", "success");
      await refresh();
    } catch (e) {
      showToast?.("Couldn't turn off notifications: " + e.message, "error");
    } finally {
      setBusy(false);
    }
  }

  // Permission denied falls through to the hero card below, which
  // shows a "Try again" button + contextual instructions for both
  // browser-blocked and not-yet-prompted states. Earlier this early-
  // returned with text only, leaving denied users no actionable button.

  // Subscribed — confirmation row + health detail.
  // Apple silently revokes web push permissions when the SW fails to
  // call showNotification (the "silent push" trap). When that
  // happens, browser-side getSubscription() still returns a sub, the
  // server still gets 201 from APNS, and the user has no idea their
  // pushes are dead. We track health server-side via the SW beacon
  // (last_sw_received_at). Show the user the truth.
  if (state.subscribed) {
    const h = state.health;
    const lastRecv = h?.last_sw_received_at ? new Date(h.last_sw_received_at).getTime() : 0;
    const lastDispatch = h?.last_dispatch_at ? new Date(h.last_dispatch_at).getTime() : 0;
    const subAge = h?.created_at ? Date.now() - new Date(h.created_at).getTime() : 0;
    const daysSinceRecv = lastRecv ? Math.floor((Date.now() - lastRecv) / 86400000) : null;
    // Suspect cases — any of the following:
    //   1. Browser has a sub locally but server has no row → the
    //      DB row was wiped (admin reset, expired sweep) and the
    //      App.js auto-resubscribe didn't kick in for some reason.
    //   2. Server marked the sub dead (>7d without SW beacon).
    //   3. Sub has been receiving dispatches >7d with zero beacons.
    //   4. Last beacon was >7d ago.
    const SEVEN_DAYS = 7 * 86400000;
    const dbRowMissing = !h;
    const dbDeadFlag = !!h?.dead_marked_at;
    const neverAcked = !lastRecv && lastDispatch > 0 && subAge > SEVEN_DAYS;
    const stale = lastRecv > 0 && (Date.now() - lastRecv) > SEVEN_DAYS;
    const suspect = dbRowMissing || dbDeadFlag || neverAcked || stale;
    const tone = suspect ? "warn" : "positive";
    const palette = suspect
      ? { bg: "bg-warn-50/40", border: "border-warn-200", text: "text-warn-800", icon: "warning" }
      : { bg: "bg-positive-50/40", border: "border-positive-200", text: "text-positive-800", icon: "check_circle" };
    const lastRecvLabel = dbRowMissing
      ? "This device's subscription is missing on the server — tap Re-enable."
      : !lastRecv
      ? (lastDispatch > 0 ? "Never received — your device may have stopped accepting them." : "No pushes sent yet — wait for the next event.")
      : daysSinceRecv === 0 ? "Last received: today"
      : daysSinceRecv === 1 ? "Last received: yesterday"
      : "Last received: " + daysSinceRecv + " days ago";
    return (
      <Card padding="p-4" className={"mb-5 " + palette.bg + " " + palette.border}>
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div className="min-w-0 flex-1">
            <div className={"flex items-center gap-2 text-sm " + palette.text}>
              <span className="material-icons-outlined text-base">{palette.icon}</span>
              <span className="font-semibold">{suspect ? "Push notifications appear broken" : "Notifications are on for this device"}</span>
            </div>
            <div className={"text-xs mt-1 " + palette.text + " opacity-80"}>{lastRecvLabel}</div>
            {suspect && (
              <div className="text-xs text-neutral-600 mt-2">
                Apple silently disables push notifications for installed PWAs after certain failure conditions. Tap <strong>Re-enable</strong> to register a fresh subscription.
              </div>
            )}
          </div>
          <div className="flex gap-2 flex-wrap shrink-0">
            <Btn onClick={handleTest} disabled={busy} variant="secondary" size="xs">Send test</Btn>
            {suspect && <Btn onClick={handleEnable} disabled={busy} variant="primary" size="xs">{busy ? "Working…" : "Re-enable"}</Btn>}
            <Btn onClick={handleDisable} disabled={busy} variant="secondary" size="xs">Turn off</Btn>
          </div>
        </div>
      </Card>
    );
  }

  // Not subscribed — prominent hero card. Always shows the OS-level
  // permission prompt button so users can tap it any time, including
  // after they previously dismissed the auto-prompt at login.
  return (
    <Card padding="p-5" className="mb-5 border-brand-200 bg-brand-50/40">
      <div className="flex items-start gap-3">
        <span className="material-icons-outlined text-brand-600 text-2xl">notifications_active</span>
        <div className="flex-1 min-w-0">
          <div className="text-base font-semibold text-neutral-800 mb-1">Turn on push notifications</div>
          <div className="text-sm text-neutral-600 mb-3">
            Get instant alerts on this device when a tenant pays, sends a message, or files a maintenance request.
            {state.permission === "denied" && (
              <span className="block mt-2 text-warn-700 text-xs">
                Notifications are blocked at the browser level. Open your browser's site settings for this page → Notifications → Allow, then tap the button below to subscribe.
              </span>
            )}
          </div>
          <Btn onClick={handleEnable} disabled={busy} size="lg">
            {busy ? "Requesting permission…" : (state.permission === "denied" ? "Try again" : "Allow notifications")}
          </Btn>
          {/iPhone|iPad|iPod/i.test(typeof navigator !== "undefined" ? navigator.userAgent : "") && (
            <div className="mt-3 text-xs text-neutral-500">
              <strong>iPhone / iPad:</strong> notifications only work when Housify is added to your home screen.
              In Safari, tap Share → "Add to Home Screen", open the app from there, then tap Allow notifications.
            </div>
          )}
        </div>
      </div>
      {state.log.length > 0 && (
        <details className="mt-3">
          <summary className="text-xs text-neutral-400 cursor-pointer hover:text-neutral-600">Show technical details</summary>
          <pre className="mt-2 bg-neutral-50 rounded-lg p-3 text-2xs text-neutral-600 tnum whitespace-pre-wrap max-h-48 overflow-y-auto">{state.log.join("\n")}</pre>
        </details>
      )}
    </Card>
  );
}

// Master event registry — single source of truth for the human label,
// icon, and description shown on the Notifications page AND the Admin
// → Notifications rule editor. Add new types here as they're wired
// into queueNotification call sites.
//
// `category` groups rules into sections in the admin editor.
// `vars` lists the {{tokens}} the worker substitutes when rendering;
// shown to admins below the body editor so they know what they can
// reference.
export const eventLabels = {
  // Tenant lifecycle
  move_in:             { label: "Move-in welcome",         icon: "🏠", desc: "When a tenant is added to a property",        category: "Tenant lifecycle", vars: ["tenant","property","moveInDate","company_name","app_url"] },
  move_out:            { label: "Move-out completed",      icon: "🚪", desc: "When move-out wizard runs",                    category: "Tenant lifecycle", vars: ["tenant","property","moveOutDate","company_name","app_url"] },
  lease_created:       { label: "Lease created",           icon: "📝", desc: "When a new lease is saved",                    category: "Tenant lifecycle", vars: ["tenant","property","startDate","endDate","rent","company_name","app_url"] },
  lease_expiring:      { label: "Lease expiring",          icon: "⏰", desc: "Cron — N days before lease end",               category: "Tenant lifecycle", vars: ["tenant","property","date","daysLeft","company_name","app_url"] },
  lease_expiry:        { label: "Lease expiry alert",      icon: "⏰", desc: "Cron — 60 days before lease end",              category: "Tenant lifecycle", vars: ["tenant","property","date","daysLeft","company_name","app_url"] },
  // Money
  rent_due:            { label: "Rent due reminder",       icon: "💰", desc: "Cron — N days before rent is due",             category: "Money",            vars: ["tenant","property","amount","date","month","company_name","app_url"] },
  rent_overdue:        { label: "Rent past due",           icon: "🔴", desc: "When a payment goes past its grace period",    category: "Money",            vars: ["tenant","property","balance","company_name","app_url"] },
  payment_received:    { label: "Payment received (tenant)", icon: "✅", desc: "Tenant-facing thank-you — Stripe + manual",  category: "Money",            vars: ["tenant","amount","date","property","company_name","app_url"] },
  payment_received_admin: { label: "Payment received (staff)", icon: "✅", desc: "Staff heads-up after a tenant pays",       category: "Money",            vars: ["tenant","amount","date","property","company_name","app_url"] },
  payment_failed:      { label: "Autopay failed (tenant)", icon: "❌", desc: "Off-session autopay decline — to tenant",     category: "Money",            vars: ["tenant","amount","date","property","error","company_name","app_url"] },
  payment_failed_admin:{ label: "Autopay failed (staff)",  icon: "❌", desc: "Off-session autopay decline — to staff",      category: "Money",            vars: ["tenant","amount","date","property","error","company_name","app_url"] },
  late_fee_applied:    { label: "Late fee applied",        icon: "💸", desc: "When admin applies a late fee",                category: "Money",            vars: ["tenant","property","amount","daysLate","balance","company_name","app_url"] },
  // Maintenance
  work_order_created:  { label: "Maintenance request created", icon: "🔧", desc: "When admin creates a work order",          category: "Maintenance",      vars: ["tenant","property","issue","priority","company_name","app_url"] },
  work_order_completed:{ label: "Maintenance completed",   icon: "🛠️", desc: "When a work order is closed",                  category: "Maintenance",      vars: ["tenant","property","issue","company_name","app_url"] },
  work_order_update:   { label: "Maintenance update",      icon: "🔧", desc: "Status change on an open work order",          category: "Maintenance",      vars: ["tenant","property","issue","status","company_name","app_url"] },
  inspection_due:      { label: "Inspection reminder",     icon: "🔍", desc: "Before a scheduled inspection",                category: "Maintenance",      vars: ["property","date","company_name","app_url"] },
  insurance_expiring:  { label: "Vendor insurance expiring", icon: "🛡️", desc: "Vendor coverage about to lapse",            category: "Maintenance",      vars: ["vendor","date","company_name","app_url"] },
  // Communication
  message_received:    { label: "New message",             icon: "💬", desc: "Tenant↔staff thread",                          category: "Communication",    vars: ["sender","tenant","property","preview","company_name","app_url"] },
  // Operations
  deposit_returned:    { label: "Deposit settlement",      icon: "🪙", desc: "After move-out deposit accounting",            category: "Operations",       vars: ["tenant","property","returned","deducted","company_name","app_url"] },
  approval_pending:    { label: "Approval needed",         icon: "🔔", desc: "Action requires a manager/admin review",       category: "Operations",       vars: ["summary","requester","company_name","app_url"] },
  approval_request:    { label: "Approval request",        icon: "🔔", desc: "Synonym used by some flows",                   category: "Operations",       vars: ["summary","requester","company_name","app_url"] },
  owner_statement:     { label: "Owner statement",         icon: "📊", desc: "Monthly statement available for owner",        category: "Operations",       vars: ["owner","period","net","company_name","app_url"] },
  signed_doc_copy:     { label: "Signed document copy",    icon: "📄", desc: "After eSignature completes",                   category: "Operations",       vars: ["doc_name","signer_name","company_name","app_url"] },
  document_uploaded:   { label: "Document uploaded",       icon: "📎", desc: "When a doc is added to a tenant/property",     category: "Operations",       vars: ["tenant","doc_name","property","company_name","app_url"] },
  invoice_approved:    { label: "Invoice approved",        icon: "✔️", desc: "Vendor invoice approved",                       category: "Operations",       vars: ["vendor","amount","company_name","app_url"] },
  invoice_rejected:    { label: "Invoice rejected",        icon: "✖️", desc: "Vendor invoice rejected",                       category: "Operations",       vars: ["vendor","amount","reason","company_name","app_url"] },
  error_reported:      { label: "Account error",           icon: "🚨", desc: "Critical PM-XXXX error",                       category: "Operations",       vars: ["code","context","company_name","app_url"] },
};

// Day bucket for grouping Activity / History rows. Returns "Today",
// "Yesterday", or "Earlier" — matches macOS Mail / iOS conventions
// rather than absolute dates, which read more friendly for a feed.
function dayBucket(iso) {
  if (!iso) return "Earlier";
  const d = new Date(iso);
  if (isNaN(d)) return "Earlier";
  const startOfDay = (x) => { const c = new Date(x); c.setHours(0, 0, 0, 0); return c; };
  const today = startOfDay(new Date());
  const dDay = startOfDay(d);
  const days = Math.round((today - dDay) / 86400000);
  if (days <= 0) return "Today";
  if (days === 1) return "Yesterday";
  return "Earlier";
}

function groupByDay(items, dateKey = "created_at") {
  const groups = { Today: [], Yesterday: [], Earlier: [] };
  for (const r of items) groups[dayBucket(r[dateKey])].push(r);
  return groups;
}

function EmailNotifications({ addNotification, userProfile, userRole, companyId, showToast, showConfirm }) {
  const [settings, setSettings] = useState([]);
  const [logs, setLogs] = useState([]);
  const [activity, setActivity] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("activity");
  const [activityFilter, setActivityFilter] = useState("all"); // "all" | "unread"
  const [queueFailed, setQueueFailed] = useState(0);

  // Dropped: tenants / leases / queueStats.pending+sent — the page no
  // longer surfaces those. Queue-failed count is kept only to drive the
  // single conditional inline alert at the top.
  const fetchQueueFailed = useCallback(async () => {
    try {
      const { count } = await supabase.from("notification_queue")
        .select("*", { count: "exact", head: true })
        .eq("company_id", companyId).eq("status", "failed");
      setQueueFailed(count || 0);
    } catch (e) { pmError("PM-8006", { raw: e, context: "fetch notification queue failed count", silent: true }); }
  }, [companyId]);

  const channels = ["in_app", "email", "push"];
  const channelLabels = { in_app: "In-app", email: "Email", push: "Push" };
  // eventLabels is now module-level and exported (see top of file)
  // so Admin.js can reuse the same map.

  const fetchData = useCallback(async () => {
    setLoading(true);
    // Activity feed is per-user — every notification row carries a
    // recipient_email. Without filtering, the tab would leak each
    // user's inbox to every admin.
    const myEmail = userProfile?.email || "";
    const [s, l, inbox, st] = await Promise.all([
      supabase.from("notification_settings").select("*").eq("company_id", companyId).order("event_type"),
      supabase.from("notification_log").select("*").eq("company_id", companyId).order("created_at", { ascending: false }).limit(100),
      supabase.from("notification_inbox").select("*").eq("company_id", companyId)
        .or("recipient_email.ilike." + escapeFilterValue(myEmail || "none") + ",recipient_email.is.null")
        .order("created_at", { ascending: false }).limit(200),
      // Per-user read/dismiss state. Inbox rows are shared (recipient_email may
      // be null = everyone), so read/dismissed lives here per viewer, not on
      // the row. RLS already restricts these to the current user's rows.
      supabase.from("notification_inbox_state").select("*").eq("company_id", companyId),
    ]);
    const stateMap = {};
    (st.data || []).forEach(x => { stateMap[x.inbox_id] = x; });
    const merged = (inbox.data || []).map(n => {
      const stt = stateMap[n.id] || null;
      return { ...n, _state: stt, _read: !!(stt && stt.read_at), _dismissed: !!(stt && stt.dismissed_at) };
    }).filter(n => !n._dismissed);
    setSettings(s.data || []);
    setLogs(l.data || []);
    setActivity(merged);
    setLoading(false);
  }, [companyId, userProfile?.email]);

  useEffect(() => { fetchData(); }, [fetchData]);
  useEffect(() => { fetchQueueFailed(); }, [fetchQueueFailed]);

  // Per-user read/dismiss. Upserts the whole state row (merged with what the
  // feed already loaded) so setting read_at never clobbers dismissed_at.
  const myEmail = userProfile?.email || "";
  async function setInboxState(n, patch) {
    const ex = n._state || {};
    const row = {
      company_id: companyId, inbox_id: n.id, user_email: myEmail,
      read_at: ex.read_at || null, dismissed_at: ex.dismissed_at || null,
      ...patch, updated_at: new Date().toISOString(),
    };
    const { error } = await supabase.from("notification_inbox_state").upsert(row, { onConflict: "inbox_id,user_email" });
    if (error) { showToast?.("Couldn't update notification: " + error.message, "error"); return; }
    fetchData();
  }
  const markRead = (n) => setInboxState(n, { read_at: new Date().toISOString() });
  const dismissNote = (n) => setInboxState(n, { dismissed_at: new Date().toISOString() });
  async function markAllRead() {
    const now = new Date().toISOString();
    const unread = activity.filter(a => !a._read);
    if (!unread.length) return;
    const rows = unread.map(n => ({
      company_id: companyId, inbox_id: n.id, user_email: myEmail,
      read_at: now, dismissed_at: (n._state && n._state.dismissed_at) || null, updated_at: now,
    }));
    const { error } = await supabase.from("notification_inbox_state").upsert(rows, { onConflict: "inbox_id,user_email" });
    if (error) { showToast?.("Couldn't mark all read: " + error.message, "error"); return; }
    fetchData();
  }

  async function toggleSetting(setting) {
    const { error } = await supabase.from("notification_settings").update({ enabled: !setting.enabled }).eq("company_id", companyId).eq("id", setting.id);
    if (error) pmError("PM-8006", { raw: error, context: "notification_settings toggle", silent: true });
    fetchData();
  }

  async function updateDaysBefore(setting, days) {
    // The control is only rendered while days_before > 0, so writing the
    // 0 that a cleared field produces would make the field vanish with no
    // way to get it back — and a typed "-5" was being persisted verbatim
    // as "notify -5 days early". Refuse both, out loud, and write nothing.
    const n = Number(days);
    if (days === "" || days == null || !Number.isFinite(n) || n < 1) {
      showToast?.("Enter how many days early to notify (1 or more).", "error");
      return;
    }
    const { error } = await supabase.from("notification_settings").update({ days_before: Math.round(n) }).eq("company_id", companyId).eq("id", setting.id);
    if (error) pmError("PM-8006", { raw: error, context: "notification_settings days_before", silent: true });
    fetchData();
  }

  async function updateTemplate(setting, template) {
    const { error } = await supabase.from("notification_settings").update({ template }).eq("company_id", companyId).eq("id", setting.id);
    if (error) pmError("PM-8006", { raw: error, context: "notification_settings template", silent: true });
  }

  async function updateRecipients(setting, recipients) {
    const { error } = await supabase.from("notification_settings").update({ recipients }).eq("id", setting.id).eq("company_id", companyId);
    if (error) pmError("PM-8006", { raw: error, context: "notification_settings recipients", silent: true });
    fetchData();
  }

  // Supabase returns JSONB as parsed JS objects; older rows stored
  // serialized strings. Accept either so a mixed set doesn't crash.
  function parseChannels(x) {
    if (!x) return { in_app: true, email: true, push: true };
    if (typeof x === "object") return x;
    try { return JSON.parse(x); } catch { return { in_app: true, email: true, push: true }; }
  }

  async function toggleChannel(setting, ch) {
    const current = parseChannels(setting.channels);
    const next = { ...current, [ch]: !current[ch] };
    const { error } = await supabase.from("notification_settings").update({ channels: next }).eq("id", setting.id).eq("company_id", companyId);
    if (error) pmError("PM-8006", { raw: error, context: "notification_settings channels", silent: true });
    fetchData();
  }

  async function sendTestNotification(setting) {
    const testRecipient = userProfile?.email || "test@example.com";
    const { error } = await supabase.from("notification_log").insert([{
      company_id: companyId,
      event_type: setting.event_type,
      recipient_email: normalizeEmail(testRecipient),
      subject: "[TEST] " + (eventLabels[setting.event_type]?.label || setting.event_type),
      message: setting.template || "Test notification",
      status: "sent",
      related_id: "test",
    }]);
    if (error) pmError("PM-8006", { raw: error, context: "notification_log test write", silent: true });
    addNotification("✉️", "Test sent for " + (eventLabels[setting.event_type]?.label || setting.event_type));
    fetchData();
  }

  if (loading) return <Spinner />;

  // Activity filter (All / Unread)
  const filteredActivity = activityFilter === "unread" ? activity.filter(a => !a._read) : activity;
  const unreadCount = activity.filter(a => !a._read).length;
  const activityGroups = groupByDay(filteredActivity);
  const logGroups = groupByDay(logs);

  return (
    <div>
      <PageHeader
        title="Notifications"
        subtitle="Stay up to date on rent, leases, and maintenance."
      />

      {queueFailed > 0 && (
        <div className="bg-danger-50 border border-danger-200 rounded-xl px-4 py-2.5 mb-4 text-sm text-danger-700">
          {queueFailed} notification{queueFailed === 1 ? "" : "s"} didn't deliver. Check your email settings.
        </div>
      )}

      <DevicePushPanel companyId={companyId} userProfile={userProfile} showToast={showToast} />

      <div className="flex gap-1 mb-4 border-b border-brand-50">
        <TabBar tabs={[["activity", "Activity"], ["preferences", "Preferences"], ["history", "History"]]} active={activeTab} onChange={setActiveTab} size="md" />
      </div>

      {/* ─── ACTIVITY ─── */}
      {activeTab === "activity" && (
        <div>
          <div className="flex gap-2 mb-4 items-center">
            <FilterPill active={activityFilter === "all"} onClick={() => setActivityFilter("all")}>
              All ({activity.length})
            </FilterPill>
            <FilterPill active={activityFilter === "unread"} onClick={() => setActivityFilter("unread")}>
              Unread ({unreadCount})
            </FilterPill>
            {unreadCount > 0 && <TextLink tone="brand" size="xs" className="ml-auto" onClick={markAllRead}>Mark all read</TextLink>}
          </div>

          {filteredActivity.length === 0 ? (
            <div className="text-center py-12 text-neutral-400 text-sm">
              {activityFilter === "unread" ? "No unread notifications." : "No activity yet. As things happen, you'll see them here."}
            </div>
          ) : (
            ["Today", "Yesterday", "Earlier"].map(bucket => (
              activityGroups[bucket].length > 0 && (
                <div key={bucket} className="mb-5">
                  <div className="text-xs uppercase tracking-wide text-neutral-400 font-semibold mb-2 px-1">{bucket}</div>
                  <div className="space-y-2">
                    {activityGroups[bucket].map(n => (
                      <Card key={n.id} padding="px-4 py-3" className={"flex items-center gap-3" + (n._read ? " opacity-60" : "")}>
                        <span className="text-xl">{n.icon || "🔔"}</span>
                        <div className="flex-1 min-w-0">
                          <div className="text-sm text-neutral-800 truncate">{n.message}</div>
                          <div className="text-xs text-neutral-400">
                            {new Date(n.created_at).toLocaleTimeString([], { hour: "numeric", minute: "2-digit" })}
                            {n.notification_type && n.notification_type !== "general" ? " · " + n.notification_type : ""}
                          </div>
                        </div>
                        {!n._read && <Badge color="indigo" label="New" />}
                        {!n._read && <TextLink tone="brand" size="xs" onClick={() => markRead(n)}>Mark read</TextLink>}
                        <TextLink tone="neutral" size="xs" onClick={() => dismissNote(n)} aria-label="Dismiss">Dismiss</TextLink>
                      </Card>
                    ))}
                  </div>
                </div>
              )
            ))
          )}
        </div>
      )}

      {/* ─── PREFERENCES ─── */}
      {activeTab === "preferences" && (
        <div className="space-y-3">
          {settings.length === 0 && (
            <EmptyState size="page" title={"No notification rules configured for this company yet."} />
          )}
          {settings.map(s => {
            const info = eventLabels[s.event_type] || { label: s.event_type, icon: "📧", desc: "" };
            const ch = parseChannels(s.channels);
            return (
              <Card key={s.id} padding="p-4" className={s.enabled ? "" : "opacity-70"}>
                {/* Header row — always visible */}
                <div className="flex items-start justify-between gap-3">
                  <div className="flex items-start gap-3 min-w-0 flex-1">
                    <span className="text-xl shrink-0">{info.icon}</span>
                    <div className="min-w-0">
                      <div className="text-sm font-semibold text-neutral-800">{info.label}</div>
                      <div className="text-xs text-neutral-400">{info.desc}</div>
                    </div>
                  </div>
                  <Switch checked={s.enabled} onChange={() => toggleSetting(s)}
                    label={(s.enabled ? "Disable " : "Enable ") + info.label} />
                </div>

                {/* Detail controls — only when enabled */}
                {s.enabled && (
                  <div className="mt-4 pt-4 border-t border-brand-50 space-y-3">
                    {/* Channels */}
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="text-xs text-neutral-500 mr-1">Send via:</span>
                      {channels.map(c => (
                        <button key={c} onClick={() => toggleChannel(s, c)}
                          className={"text-xs px-2.5 py-1 rounded-full border transition-colors " +
                            (ch[c]
                              ? "bg-brand-50 text-brand-700 border-brand-200"
                              : "bg-white text-neutral-400 border-neutral-200 hover:border-neutral-300")}>
                          {channelLabels[c]}
                        </button>
                      ))}
                    </div>

                    {/* Recipients + days_before */}
                    <div className="flex items-center gap-3 flex-wrap text-xs">
                      <div className="flex items-center gap-2">
                        <span className="text-neutral-500">Recipients:</span>
                        <Select filter value={s.recipients || "all"} onChange={e => updateRecipients(s, e.target.value)}
                          className="text-xs border border-neutral-200 rounded-lg px-2 py-1">
                          <option value="all">Everyone</option>
                          <option value="tenant">Tenants only</option>
                          <option value="admin">Admins only</option>
                          <option value="tenant,admin">Admins + Tenants</option>
                        </Select>
                      </div>
                      {s.days_before > 0 && (
                        <div className="flex items-center gap-2">
                          <span className="text-neutral-500">Notify</span>
                          <Input type="number" value={s.days_before} onChange={e => updateDaysBefore(s, e.target.value)}
                            className="w-14 border border-neutral-200 rounded-lg px-2 py-1 text-xs text-center" min="0" />
                          <span className="text-neutral-500">days early</span>
                        </div>
                      )}
                    </div>

                    {/* Customize message + Send test */}
                    <div className="flex items-center justify-between text-xs">
                      <details className="flex-1 mr-3">
                        <summary className="text-neutral-500 cursor-pointer hover:text-neutral-700 inline">
                          Customize message
                        </summary>
                        <Textarea value={s.template || ""} onChange={e => updateTemplate(s, e.target.value)}
                          className="text-xs text-neutral-700 mt-2" rows={3}
                          placeholder="Use {{tenant}}, {{property}}, {{amount}}, {{date}} as placeholders." />
                      </details>
                      <button onClick={() => sendTestNotification(s)}
                        className="text-brand-600 hover:underline text-xs shrink-0">
                        Send test
                      </button>
                    </div>
                  </div>
                )}
              </Card>
            );
          })}
        </div>
      )}

      {/* ─── HISTORY ─── */}
      {activeTab === "history" && (
        <div>
          {logs.length === 0 ? (
            <EmptyState size="page" title={"No notifications sent yet."} />
          ) : (
            ["Today", "Yesterday", "Earlier"].map(bucket => (
              logGroups[bucket].length > 0 && (
                <div key={bucket} className="mb-5">
                  <div className="text-xs uppercase tracking-wide text-neutral-400 font-semibold mb-2 px-1">{bucket}</div>
                  <div className="space-y-2">
                    {logGroups[bucket].map(l => (
                      <Card key={l.id} padding="px-4 py-2.5" className="flex items-center justify-between gap-3">
                        <div className="min-w-0 flex-1">
                          <div className="text-sm text-neutral-800 truncate">{l.subject}</div>
                          <div className="text-xs text-neutral-400 truncate">
                            {l.recipient_email} · {new Date(l.created_at).toLocaleTimeString([], { hour: "numeric", minute: "2-digit" })}
                          </div>
                        </div>
                        <Badge status={l.status} />
                      </Card>
                    ))}
                  </div>
                </div>
              )
            ))
          )}
        </div>
      )}
    </div>
  );
}

export { EmailNotifications };
